import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PaymentModal } from "./Payments";
import { useInvoicePilot } from "./InvoicePilotLink";
import { useCorrections } from './Corrections';
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Card,
  ErrorState,
  Money,
  PageHeader,
  Spinner,
  StatusBadge,
} from "../components/ui";
import { formatDate } from "../lib/format";
import type { InvoiceDetail as InvoiceDetailModel } from "../lib/models";

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();
  const corrections = useCorrections();
  const [params, setParams] = useSearchParams();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [receiptKey, setReceiptKey] = useState<string | null>(null);
  const closePayment = () => { setPaymentOpen(false); setParams({}, { replace: true }); };

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", id, currentOrg?.id],
    queryFn: () => api.get<InvoiceDetailModel>(`/invoices/${id}`),
    enabled: !!id && !!currentOrg,
  });

  const voidInvoice = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/void`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  return (
    <div>
      <PageHeader
        title={`Invoice ${data.docNumber ?? ""}`}
        subtitle={`Dated ${formatDate(data.txnDate)}`}
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate("/invoices")}>
              Back
            </Button>
            {corrections.data?.enabled && data.status !== 'void' && <Button variant="danger" onClick={()=>navigate(`/corrections?action=void_invoice&target=${encodeURIComponent(data.id)}`)}>Void invoice</Button>}
            {!corrections.data?.available && data.status !== "void" && data.status !== "paid" && (
              <Button
                variant="danger"
                disabled={voidInvoice.isPending}
                onClick={() => voidInvoice.mutate()}
              >
                Void
              </Button>
            )}
          </div>
        }
      />

      {pilot.data?.enabled && <div className="mb-4 rounded-lg border border-brand-100 bg-brand-50 p-4">
        {receiptKey === `${currentOrg?.id}:${id}` ? <p role="status">Payment recorded. {data.balance > 0 ? <>Remaining balance: <Money cents={data.balance} currency={currency} />.</> : "This invoice is fully paid."}</p> :
          <p>{data.status === "void" ? "This invoice is void." : data.balance > 0 ? "When the customer pays, record the amount received against this invoice." : "This invoice has no remaining balance."}</p>}
        {data.balance > 0 && data.status !== "void" && <Button className="mt-3" onClick={() => setPaymentOpen(true)}>Record payment</Button>}
      </div>}

      {pilot.data?.enabled && data.balance > 0 && data.status !== "void" && (paymentOpen || params.get("payment") === "1") &&
        <PaymentModal key={`${currentOrg?.id}:${id}`} open invoice={{id:data.id,customerId:data.customerId,docNumber:data.docNumber}}
          onClose={closePayment} onSaved={() => {
            closePayment();
            void Promise.all([
              qc.invalidateQueries({queryKey:["invoice",id,currentOrg?.id]}),
              qc.invalidateQueries({queryKey:["invoices"]}),
              qc.invalidateQueries({queryKey:["payments"]}),
            ]).then(() => setReceiptKey(`${currentOrg?.id}:${id}`));
          }}/>
      }

      <Card className="p-6">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <StatusBadge status={data.status} />
            {data.dueDate && (
              <span className="text-sm text-gray-500">
                Due {formatDate(data.dueDate)}
              </span>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Balance due</p>
            <p className="text-2xl font-semibold">
              <Money cents={data.balance} currency={currency} />
            </p>
          </div>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="th">Description</th>
              <th className="th text-right">Qty</th>
              <th className="th text-right">Rate</th>
              <th className="th text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.lines.map((l) => (
              <tr key={l.id}>
                <td className="td">{l.description ?? "—"}</td>
                <td className="td text-right">{l.qty}</td>
                <td className="td text-right">
                  <Money cents={l.unitPrice} currency={currency} />
                </td>
                <td className="td text-right">
                  <Money cents={l.amount} currency={currency} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <dl className="w-64 space-y-1 text-sm">
            <Row label="Subtotal" cents={data.subtotal} currency={currency} />
            <Row label="Tax" cents={data.taxTotal} currency={currency} />
            <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold">
              <dt>Total</dt>
              <dd>
                <Money cents={data.total} currency={currency} />
              </dd>
            </div>
          </dl>
        </div>

        {data.memo && (
          <p className="mt-6 text-sm text-gray-500">Memo: {data.memo}</p>
        )}
      </Card>
    </div>
  );
}

function Row({
  label,
  cents,
  currency,
}: {
  label: string;
  cents: number;
  currency: string;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd>
        <Money cents={cents} currency={currency} />
      </dd>
    </div>
  );
}
